/** @module Components */
import React from 'react'
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState
} from '@tanstack/react-table'

import { SearchBar, Spinner, CustomTableHeader, CustomTableRow } from '..'

/** @category Prop Types */
export type TableProps = {
  columns: ColumnDef<Record<string, unknown>>[]
  data: Record<string, unknown>[]
  selectedPageSize: string
  setSelectedPageSize: (pageSize: string) => void
  fetching: boolean
  headerRowClassName?: string
  headerCellClassName?: string
  customRowClassName?: string
  customRowCellClassName?: string
  searchable?: boolean
}

export const Table: React.FC<TableProps> = ({
  columns,
  data,
  selectedPageSize,
  setSelectedPageSize,
  fetching,
  headerRowClassName,
  headerCellClassName,
  customRowClassName,
  customRowCellClassName,
  searchable = true
}) => {
  const [globalFilter, setGlobalFilter] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([])

  const table = useReactTable({
    columns,
    data,
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: parseInt(selectedPageSize)
      },
      globalFilter,
      sorting
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  })

  const {
    getRowModel,
    getHeaderGroups,
    getCanNextPage,
    getCanPreviousPage,
    getPageCount,
    getState,
    nextPage,
    previousPage,
    setPageSize,
    getPageOptions
  } = table

  const { pageIndex, pageSize } = getState().pagination
  const pageOptions = getPageOptions()
  const page = getRowModel().rows
  const headerGroups = getHeaderGroups()
  const canNextPage = getCanNextPage()
  const canPreviousPage = getCanPreviousPage()
  const pageCount = getPageCount()

  return (
    <div className="max-w-8xl mx-auto w-full pb-10">
      {searchable && (
        <SearchBar
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
      )}
      <div className="flex flex-col mx-8">
        <div className="align-middle min-w-full overflow-x-auto overflow-hidden border border-[#9298a8] rounded-md shadow-md">
          <table className="w-full">
            <CustomTableHeader
              headerGroups={headerGroups}
              headerRowClassName={`bg-[#142841] text-white ${
                headerRowClassName || ''
              }`}
              headerCellClassName={headerCellClassName}
            />
            <CustomTableRow
              rows={page}
              customRowClassName={`even:bg-[#EDE6DE] hover:bg-[#A0B2B4]/20 ${
                customRowClassName || ''
              }`}
              customRowCellClassName={customRowCellClassName}
            />
          </table>
          {(fetching && (
            <div className="py-4" data-testid="loading-container">
              <div
                className="flex justify-center"
                data-testid="loading-spinner"
              >
                <Spinner />
              </div>
            </div>
          )) ||
            (!fetching && !page.length && (
              <div className="py-4" data-testid="default-message-container">
                <p
                  className="text-center text-[#3C2416]"
                  data-testid="empty-table-message"
                >
                  No information found
                </p>
              </div>
            ))}
          {pageCount > 1 && (
            <footer
              className="bg-[#EDE6DE] flex justify-between px-6 border-t border-[#9298a8]"
              data-testid="table-footer"
            >
              <div
                className="flex w-1/2 py-3 items-center"
                data-testid="results-container"
              >
                <p
                  className="font-medium text-base text-[#3C2416] tracking-wide"
                  data-testid="showing-results-text"
                >
                  Showing{' '}
                  {page.length > 0 ? parseInt(page[0].id as string) + 1 : 0} to{' '}
                  {page.length > 0
                    ? parseInt(page[page.length - 1].id as string) + 1
                    : 0}{' '}
                  of {data.length} results
                </p>
              </div>
              <div
                className="py-3 pr-2 w-1/2 flex justify-end items-center"
                data-testid="pagination-container"
              >
                <select
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(parseInt(event.target.value))
                    setSelectedPageSize(event.target.value)
                  }}
                  className="font-medium text-base text-[#3C2416] block h-9 pl-3 py-0.5 bg-white border-[#9298a8] focus:outline-none focus:ring-[#A6410A] focus:border-[#A6410A] sm:text-sm rounded-md mr-4"
                  data-testid="page-size-select"
                >
                  {[10, 20, 30, 40, 50].map((number) => (
                    <option
                      key={number}
                      value={number}
                      data-testid="page-size-option"
                    >
                      {number} per page
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!canPreviousPage}
                  className={`font-medium text-base tracking-wide px-2 ${
                    canPreviousPage
                      ? 'text-[#A6410A] hover:text-[#3C2416]'
                      : 'text-[#9298a8]'
                  }`}
                  data-testid="first-page-button"
                >
                  {'<<'}
                </button>
                <button
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  className={`font-medium text-base tracking-wide px-1 ${
                    canPreviousPage
                      ? 'text-[#A6410A] hover:text-[#3C2416]'
                      : 'text-[#9298a8]'
                  }`}
                  data-testid="previous-page-button"
                >
                  {'<'}
                </button>
                <p
                  className="py-3 px-2 font-medium text-base text-[#3C2416] tracking-wide"
                  data-testid="result-counter-text"
                >
                  Page {pageIndex + 1} of {pageOptions.length}
                </p>
                <button
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  className={`font-medium text-base tracking-wide px-1 ${
                    canNextPage
                      ? 'text-[#A6410A] hover:text-[#3C2416]'
                      : 'text-[#9298a8]'
                  }`}
                  data-testid="next-page-button"
                >
                  {'>'}
                </button>
                <button
                  onClick={() => table.setPageIndex(pageCount - 1)}
                  disabled={!canNextPage}
                  className={`font-medium text-base tracking-wide px-2 ${
                    canNextPage
                      ? 'text-[#A6410A] hover:text-[#3C2416]'
                      : 'text-[#9298a8]'
                  }`}
                  data-testid="last-page-button"
                >
                  {'>>'}
                </button>
              </div>
            </footer>
          )}
        </div>
      </div>
    </div>
  )
}
